// src/ui/components/slideshow-view.js

import { LitElement, html } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { slideshowStyles } from "ui/styles/slideshow.css.js";
import { glassStyles } from "ui/styles/glass.css.js";
import { SlideshowState } from "core/slideshow-state.js";

export class SlideshowView extends LitElement {
  static styles = [glassStyles, slideshowStyles];

  static properties = {
    zoomedMedia: { type: Object },
    layout: { type: Object },
    slice: { type: Array },
    activeKey: { type: Number },
    transitionStyle: { type: String },
    fadeTransitionTime: { type: Number },
    fit: { type: Boolean },
  };

  zoomScale = 1;
  zoomX = 0;
  zoomY = 0;
  isPanning = false;
  panStartX = 0;
  panStartY = 0;

  constructor() {
    super();

    this.state = null;
    this.fit = false;
    this.layout = null;
    this.slice = [];
    this.activeKey = 0;
    this.fadeTransitionTime = 0.7;
    this.transitionStyle = "soft-glass";

    this.layers = [
      { layout: null, slice: [] },
      { layout: null, slice: [] },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this._onKeyDown);
    super.disconnectedCallback();
  }
  //------------------------------------------------------------
  // Zoom Handlers
  //------------------------------------------------------------
  _onZoomWheel(e) {
    e.preventDefault();

    const delta = -e.deltaY;
    const zoomFactor = 0.0015;
    const newScale = this.zoomScale + delta * zoomFactor;

    const clamped = Math.min(Math.max(newScale, 1), 5);

    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const scaleRatio = clamped / this.zoomScale;

    this.zoomX = offsetX - (offsetX - this.zoomX) * scaleRatio;
    this.zoomY = offsetY - (offsetY - this.zoomY) * scaleRatio;

    this.zoomScale = clamped;
    this.requestUpdate();
  }

  _onPanStart(e) {
    if (this.zoomScale === 1) return;

    this.isPanning = true;
    this.panStartX = e.clientX - this.zoomX;
    this.panStartY = e.clientY - this.zoomY;

    window.addEventListener("mousemove", this._onPanMove);
    window.addEventListener("mouseup", this._onPanEnd);
  }

  _onPanMove = (e) => {
    if (!this.isPanning) return;

    this.zoomX = e.clientX - this.panStartX;
    this.zoomY = e.clientY - this.panStartY;
    this.requestUpdate();
  };

  _onPanEnd = () => {
    this.isPanning = false;
    window.removeEventListener("mousemove", this._onPanMove);
    window.removeEventListener("mouseup", this._onPanEnd);
  };

  // ------------------------------------------------------------
  // Keyboard Handlers
  // ------------------------------------------------------------
  _onKeyDown = (e) => {
    // Ignore when typing in inputs
    const tag = e.target.tagName.toLowerCase();
    if (tag === "input" || tag === "textarea" || e.target.isContentEditable)
      return;

    switch (e.code) {
      // SPACE — play/pause
      case "Space":
        e.preventDefault(); // prevent page scroll
        this._emit("toggle-playback");
        break;

      // LEFT — previous
      case "ArrowLeft":
        this._emit("prev");
        break;

      // RIGHT — next
      case "ArrowRight":
        this._emit("next");
        break;

      // UP — previous layout
      case "ArrowUp":
        this._emit("prev-layout");
        break;

      // DOWN — next layout
      case "ArrowDown":
        this._emit("next-layout");
        break;

      // ESC — close zoom overlay
      case "Escape":
        if (this.zoomedMedia) {
          this._closeZoom();
        }
        break;

      // F — toggle fullscreen
      case "KeyF":
        this._emit("toggle-fullscreen");
        break;

      // S — toggle shuffle
      case "KeyS":
        this._emit("shuffle");
        break;
    }
  };

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  setTransitionStyle(style) {
    this.transitionStyle = style;
  }

  setFitMode(fit) {
    this.fit = fit;
    this.requestUpdate();
  }

  setLayout(layout) {
    this.layout = layout;
    this.requestUpdate();
  }

  setLayoutAndSlice(layout, slice) {
    const outgoingIndex = this.activeKey;
    const incomingIndex = (this.activeKey + 1) % 2;

    // Pause videos in outgoing layer
    const outgoing = this.renderRoot?.querySelector(
      `.img-layer.layer-${outgoingIndex}`,
    );
    if (outgoing) {
      outgoing.querySelectorAll("video").forEach((v) => v.pause());
    }

    // Store new content in the incoming layer
    this.layers[incomingIndex] = { layout, slice };

    // Keep external props in sync if needed
    this.layout = layout;
    this.slice = slice;

    // Flip active layer (this triggers the crossfade)
    this.activeKey = incomingIndex;

    this.requestUpdate();
  }

  // ------------------------------------------------------------
  // Compute spanning regions from DSL areas
  // ------------------------------------------------------------
  _computeRegions(areas) {
    const regions = {};

    for (let r = 0; r < areas.length; r++) {
      for (let c = 0; c < areas[r].length; c++) {
        const name = areas[r][c];

        if (!regions[name]) {
          regions[name] = {
            minRow: r,
            maxRow: r,
            minCol: c,
            maxCol: c,
          };
        } else {
          regions[name].maxRow = Math.max(regions[name].maxRow, r);
          regions[name].maxCol = Math.max(regions[name].maxCol, c);
        }
      }
    }

    return regions;
  }

  // ------------------------------------------------------------
  // Drag and Drop Handlers
  // ------------------------------------------------------------

  _onDragOver(e) {
    e.preventDefault();
    this.dragActive = true;
    this.requestUpdate();
  }

  _onDragLeave(e) {
    this.dragActive = false;
    this.requestUpdate();
  }

  async _onDrop(e) {
    e.preventDefault();
    this.dragActive = false;

    const files = [...e.dataTransfer.files];

    // Filter to images only
    const images = files.filter(
      (f) =>
        f.type.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(f.name),
    );

    if (images.length === 0) return;

    // Convert File objects into your media format
    const mediaItems = await Promise.all(
      images.map(async (file) => ({
        type: "image",
        url: URL.createObjectURL(file),
        name: file.name,
        file,
      })),
    );

    // Merge into your slideshow state
    this.media = [...this.state.media, ...mediaItems];
    this.state.setMedia(this.media);
    this.requestUpdate();
  }

  // ------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------
  render() {
    return html`
      ${this.slice.length === 0
        ? html`
            <div
              class="empty-help drop-zone"
              @dragover=${this._onDragOver}
              @dragleave=${this._onDragLeave}
              @drop=${this._onDrop}
            >
              <div class="empty-help">
                <div class="empty-help-box glass-heavy">
                  <h1>Slidey Slideshow</h1>
                  <wa-icon name="folder-open" size="lg"></wa-icon>
                  <h2>No images loaded</h2>
                  <p>Select a folder or drop images here.</p>
                  <ul>
                    <li>
                      <strong>Drop images</strong> directly onto this screen
                    </li>
                    <li>
                      <strong>Click “Load Folder”</strong> to choose a directory
                    </li>
                    <li>
                      <strong>Enable “Include Subfolders”</strong> to load
                      recursively
                    </li>
                    <li>
                      <strong>Use keyboard shortcuts</strong> for fast
                      navigation
                    </li>
                    <li>
                      <strong>Change "Transition Time"</strong> in seconds
                    </li>
                  </ul>

                  <div class="shortcut-hints">
                    <span><strong>Space</strong> Play/Pause</span>
                    <span><strong>← / →</strong> Prev / Next</span>
                    <span><strong>↑ / ↓</strong> Layout</span>
                    <span><strong>F</strong> Fullscreen</span>
                    <span><strong>S</strong> Shuffle</span>
                  </div>

                  <wa-button
                    size="md"
                    variant="primary"
                    @click=${() => this._emit("open-folder")}
                  >
                    <wa-icon name="folder-open"></wa-icon>
                    Load Folder
                  </wa-button>
                  <p>Copyright 2026 &copy; Martin Slater</p>
                </div>
              </div>
            </div>
          `
        : html` ${this.zoomedMedia
              ? html`
                  <div class="zoom-overlay" @click=${this._closeZoom}>
                    <div
                      class="zoom-container"
                      @wheel=${this._onZoomWheel}
                      @mousedown=${this._onPanStart}
                    >
                      ${this._renderMedia(
                        this.zoomedMedia,
                        "zoom-image",
                        true,
                        false,
                      )}
                    </div>
                  </div>
                `
              : null}

            <div class="slideshow transition-${this.transitionStyle}">
              ${this._renderLayer(0)} ${this._renderLayer(1)}
            </div>`}
    `;
  }

  _renderLayer(index) {
    const active = index === this.activeKey;
    const layer = this.layers[index];

    return html`
      <div
        class="slideshow-inner dsl-grid img-layer layer-${index} ${active
          ? "active"
          : ""}"
        style="--dur:${this.fadeTransitionTime}s"
      >
        ${layer.layout ? this._renderDSL(layer.layout, layer.slice) : null}
      </div>
    `;
  }

  _renderDSL(parsed, slice = this.slice) {
    const { grid, areas, slots } = parsed;
    const regions = this._computeRegions(areas);

    return html`
      <div
        class="dsl-grid"
        style="
        grid-template-columns: repeat(${grid.cols}, 1fr);
        grid-template-rows: repeat(${grid.rows}, 1fr);
      "
      >
        ${repeat(
          Object.keys(regions),
          (name) => name,
          (name) => {
            const region = regions[name];
            const slot = slots[name];
            const media = slice[slot.index];

            return html`
              <div
                class="dsl-cell"
                style="
                grid-row:${region.minRow + 1} / ${region.maxRow + 2};
                grid-column:${region.minCol + 1} / ${region.maxCol + 2};
              "
              >
                ${this._renderMedia(media, "", true)}
              </div>
            `;
          },
        )}
      </div>
    `;
  }

  _openZoom(media) {
    this.zoomedMedia = media;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const applyDimensions = (iw, ih) => {
      // The overlay may have been closed while the image/video metadata
      // was still loading — don't resurrect it with a stray zoom-open.
      if (this.zoomedMedia !== media) return;

      const fitScale = Math.min(vw / iw, vh / ih);

      this.baseScale = fitScale;
      this.zoomScale = 1;

      const displayW = iw * fitScale;
      const displayH = ih * fitScale;

      this.zoomX = (vw - displayW) / 2;
      this.zoomY = (vh - displayH) / 2;

      this._emit("zoom-open", media);
      this.requestUpdate();
    };

    if (media.type === "image") {
      const img = new Image();
      img.src = media.url;
      img.onload = () => applyDimensions(img.width, img.height);
    } else if (media.type === "video") {
      const vid = document.createElement("video");
      vid.src = media.url;
      vid.preload = "metadata";

      vid.onloadedmetadata = () => {
        applyDimensions(vid.videoWidth, vid.videoHeight);
      };
    }
  }
  _closeZoom() {
    this.zoomedMedia = null;
    this.zoomScale = 1;
    this.zoomX = 0;
    this.zoomY = 0;

    this._emit("zoom-close");
  }

  _renderMedia(
    media,
    extraClasses = "",
    showVideoControls = false,
    clickToZoom = true,
  ) {
    var transform = ``;

    if (media === this.zoomedMedia) {
      transform = `translate(${this.zoomX}px, ${this.zoomY}px) scale(${this.baseScale * this.zoomScale})`;
    }

    if (!media) {
      return null;
    }

    // Already-zoomed image must not reopen itself — clicks should only
    // bubble up to the overlay's close handler.
    const onClick = clickToZoom ? () => this._openZoom(media) : null;

    if (media.type === "video") {
      return html`
        <video
          ?controls=${showVideoControls}
          src=${media.url}
          autoplay
          loop
          muted
          playsinline
          class="media-video ${this.fit ? "fit" : "fill"} ${extraClasses}"
          @click=${onClick}
          style="transform: ${transform}"
        ></video>
      `;
    }

    return html`
      <img
        src=${media.url}
        alt=${media.name}
        class="media-image ${this.fit ? "fit" : "fill"} ${extraClasses}"
        draggable="false"
        @click=${onClick}
        style="transform: ${transform}"
      />
    `;
  }
}

customElements.define("slideshow-view", SlideshowView);
